/**
 * SquadTvTopBar — compact Stadium-board header: branding + title on the left,
 * and prize / clock / mascot on the right.
 * Background follows the org's branding: banner image when present, else the
 * org's picked primary→secondary color gradient; falls back to the squad crest
 * + Zporti dark theme when no branding is present.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { View, Text } from 'react-native';
import { Image } from 'expo-image';
import { LinearGradient } from 'expo-linear-gradient';
import { useTheme } from '../../../src/theme/useTheme';
import { getAssetUrl, ASSET_PATHS } from '../../../constants/assets';
import { WEB_IMAGE_ALT_DECORATIVE } from '../../../src/shared/helpers/imageWebAlt.helper';
import { WorldCupCrestPreview } from '../WorldCupCrestPreview';
import { deriveGroupCrest } from '../../../src/worldcup/helpers/worldCupCrest.helper';
import { getSquadTvPrizeLabel } from '../../../src/worldcup/helpers/squadTvStadium.helper';
import {
  SQUAD_TV_MODE_TEST_IDS,
  SQUAD_TV_STADIUM_COPY,
} from '../../../src/worldcup/constants/squadTvMode.constants';
import { getSquadTvTopBarStyles } from './SquadTvTopBar.styles';
import type { CrestColorId, CrestEmblemId } from '../CreateWorldCupGroupScreen/CreateWorldCupGroupScreen.constants';
import type { WorldCupGroup } from '../../../src/worldcup/types/worldcup.types';

const DEFAULT_MONKEY_URI = getAssetUrl(ASSET_PATHS.AVATARS.monkey.idle);

const formatClock = (): string =>
  new Intl.DateTimeFormat('en-US', { hour: '2-digit', minute: '2-digit' }).format(new Date());

export const SquadTvTopBar: React.FC<{ group: WorldCupGroup }> = ({ group }) => {
  const theme = useTheme();
  const styles = useMemo(() => getSquadTvTopBarStyles(theme), [theme]);
  const branding = group.companyBranding ?? null;
  const isCompany = branding !== null;

  const [clock, setClock] = useState(formatClock);
  useEffect(() => {
    const id = setInterval(() => setClock(formatClock()), 15000);
    return () => clearInterval(id);
  }, []);

  const logoUri =
    branding?.logoUrl != null && branding.logoUrl.trim().length > 0 ? branding.logoUrl.trim() : null;
  const bannerUri = branding?.bannerUrl ?? null;
  // Org's picked color drives the hero background when no banner image is set.
  const brandGradient: [string, string] | null = branding
    ? [branding.primaryColor, branding.secondaryColor ?? branding.primaryColor]
    : null;
  const mascotUri = branding?.monkeyIdleUrl
    ? branding.monkeyIdleUrl
    : branding?.monkeyIdlePath
      ? getAssetUrl(branding.monkeyIdlePath)
      : DEFAULT_MONKEY_URI;

  const crest = useMemo(() => {
    const derived = deriveGroupCrest(group.id, group.name);
    return {
      colorId: (group.crestColor as CrestColorId | null | undefined) ?? derived.colorId,
      emblemId: (group.crestEmblem as CrestEmblemId | null | undefined) ?? derived.emblemId,
    };
  }, [group.id, group.name, group.crestColor, group.crestEmblem]);

  const prizeLabel = getSquadTvPrizeLabel(branding?.rewards);
  const subtitle = isCompany
    ? `${SQUAD_TV_STADIUM_COPY.COMPANY_GROUP} · ${group.memberCount} ${SQUAD_TV_STADIUM_COPY.MEMBERS}`
    : `${group.memberCount} ${SQUAD_TV_STADIUM_COPY.MEMBERS}`;

  return (
    <View style={styles.bar} testID={SQUAD_TV_MODE_TEST_IDS.header}>
      {bannerUri ? (
        <>
          <Image
            source={{ uri: bannerUri }}
            style={styles.bannerImage}
            contentFit="cover"
            cachePolicy="memory-disk"
            alt={WEB_IMAGE_ALT_DECORATIVE}
          />
          <View style={styles.bannerScrim} pointerEvents="none" />
        </>
      ) : brandGradient ? (
        <LinearGradient
          colors={brandGradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 1 }}
          style={styles.brandGradient}
          pointerEvents="none"
        />
      ) : null}

      <View style={styles.left}>
        <View style={styles.logoBadge}>
          {logoUri ? (
            <Image
              source={{ uri: logoUri }}
              style={styles.logoImage}
              contentFit="contain"
              cachePolicy="memory-disk"
              alt={branding?.companyName ?? group.name}
            />
          ) : (
            <WorldCupCrestPreview
              colorId={crest.colorId}
              emblemId={crest.emblemId}
              name={group.name}
              size={52}
            />
          )}
        </View>
        <View style={styles.titleBlock}>
          <Text style={styles.title} numberOfLines={1}>
            {group.name}
          </Text>
          <Text style={styles.subtitle} numberOfLines={1}>
            {subtitle}
          </Text>
        </View>
      </View>

      <View style={styles.right}>
        {prizeLabel ? (
          <View style={styles.prizeLine}>
            <Text style={styles.prizeLabel}>{SQUAD_TV_STADIUM_COPY.PRIZE_LABEL}</Text>
            <Text style={styles.prizeText} numberOfLines={1}>
              {prizeLabel}
            </Text>
          </View>
        ) : null}
        <Text style={styles.clock}>{clock}</Text>
        {isCompany ? (
          <Image
            source={{ uri: mascotUri }}
            style={styles.mascot}
            contentFit="contain"
            cachePolicy="memory-disk"
            alt={WEB_IMAGE_ALT_DECORATIVE}
          />
        ) : null}
      </View>
    </View>
  );
};
